import React, { ReactNode } from "react";
import dompurify from "dompurify";
import { Entry } from "contentful";
import {
  documentToHtmlString,
  Options,
} from "@contentful/rich-text-html-renderer";
import { Document, INLINES, NodeData } from "@contentful/rich-text-types";
import { replaceTemplateString } from "./replaceTemplateString";
import { ContentBlock, Metadata } from "../models";
import { UserContextValues } from "../types";
import { Image } from "@trailhead/core-ui";
import { convertToPlainTextString } from "./convertToPlainTextString";
import { EmbeddedContentStatus } from "./checkMissingEmbeddedContent";

export const convertToHtmlString = (
  document?: Document,
  userContextValues?: UserContextValues,
): string | undefined => {
  const renderOptions: Options = {
    renderNode: {
      [INLINES.EMBEDDED_ENTRY]: node => {
        const inlineContent = node.data.target as Entry<
          ContentBlock & Metadata
        >;

        const inlineEmbeddedHtmlStr = convertToHtmlString(
          inlineContent.fields.richContent,
        );
        const plainTextStr = convertToPlainTextString(
          inlineContent.fields.richContent,
        );

        if (!inlineEmbeddedHtmlStr || !plainTextStr)
          return EmbeddedContentStatus.MISSING_INLINE_EMBEDDED_CONTENT;

        return dompurify.sanitize(inlineEmbeddedHtmlStr, {
          FORBID_TAGS: ["p"],
        });
      },
    },
  };

  if (!document) return undefined;

  let str = dompurify.sanitize(documentToHtmlString(document, renderOptions));

  if (userContextValues) {
    const templateVars = Object.fromEntries(
      Object.entries(userContextValues).filter(
        ([value]) => typeof value === "string",
      ),
    ) as Record<string, string>;
    str = replaceTemplateString(str, templateVars);
  }
  return str;
};

// TODO: Implement documentToReactComponents here

/* eslint-disable @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
export const parseIntoImage = (
  data: NodeData | undefined,
  useScrollTransition = false,
): ReactNode | undefined => {
  const fields = data?.target?.fields || data?.fields;
  if (!fields?.file.url) return undefined;
  return (
    <Image
      src={`https:${fields.file.url}`}
      size={fields.file.details.image}
      useScrollTransition={useScrollTransition}
    />
  );
};
